import React, { useState, useEffect } from 'react';
import { Check, X, Users, FileText, Save, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const ExamAttendance = () => {
  const [filters, setFilters] = useState({ 
    course_id: '', 
    semester_id: '', 
    exam_id: '', 
    subject_id: '' 
  });
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attendanceSaved, setAttendanceSaved] = useState(false);
  const [savedInfo, setSavedInfo] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchExams();
    fetchSubjects();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/examination/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ ...filters, course_id: courseId, semester_id: '', exam_id: '', subject_id: '' });
    
    if (courseId) {
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
    } else {
      setSemesters(allSemesters);
    }
    setStudents([]);
  };

  const handleSemesterChange = (semesterId) => {
    setFilters({ ...filters, semester_id: semesterId, exam_id: '', subject_id: '' });
    setStudents([]);
  };

  const loadStudents = async () => {
    if (!filters.course_id || !filters.semester_id || !filters.subject_id) {
      alert('Please select Course, Semester and Subject');
      return;
    }

    console.log('Loading students for:', filters);
    setLoading(true);
    setAttendanceSaved(false);
    setSavedInfo(null);
    setIsEditMode(false);
    
    try {
      const response = await axios.get(`${API_URL}/api/students/dropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      const filteredStudents = studentsData.filter(
        s => s.course_id === filters.course_id && s.semester_id === filters.semester_id
      );
      
      setStudents(filteredStudents);
      
      if (filteredStudents.length === 0) {
        alert('No students found for the selected Course and Semester');
        return;
      }
      
      // Check if attendance already exists for this exam+subject
      if (filters.exam_id && filters.subject_id) {
        await loadExistingAttendance(filteredStudents);
      } else {
        // Initialize as all present
        const initialAttendance = {};
        filteredStudents.forEach(student => {
          initialAttendance[student.id] = 'Present';
        });
        setAttendance(initialAttendance);
      }
      
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Failed to load students: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingAttendance = async (studentsList) => {
    if (!filters.exam_id || !filters.subject_id) {
      // No exam selected, initialize as all present
      const initialAttendance = {};
      studentsList.forEach(student => {
        initialAttendance[student.id] = 'Present';
      });
      setAttendance(initialAttendance);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/examination/attendance/get`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          exam_id: filters.exam_id,
          subject_id: filters.subject_id
        }
      });

      if (response.data.saved) {
        // Load existing attendance
        const existingAttendance = {};
        response.data.attendance.forEach(record => {
          existingAttendance[record.student_id] = record.status;
        });
        
        // Fill in any missing students as Present
        studentsList.forEach(student => {
          if (!existingAttendance[student.id]) {
            existingAttendance[student.id] = 'Present';
          }
        });
        
        setAttendance(existingAttendance);
        setAttendanceSaved(true);
        setSavedInfo({
          marked_by: response.data.marked_by,
          marked_at: response.data.marked_at
        });
        console.log('Loaded existing attendance:', response.data.count, 'records');
      } else {
        // No existing attendance, initialize as all present
        const initialAttendance = {};
        studentsList.forEach(student => {
          initialAttendance[student.id] = 'Present';
        });
        setAttendance(initialAttendance);
      }
    } catch (error) {
      console.error('Error loading existing attendance:', error);
      // Initialize as all present on error
      const initialAttendance = {};
      studentsList.forEach(student => {
        initialAttendance[student.id] = 'Present';
      });
      setAttendance(initialAttendance);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleMarkAll = (status) => {
    const newAttendance = {};
    students.forEach(student => {
      newAttendance[student.id] = status;
    });
    setAttendance(newAttendance);
  };

  const handleSaveAttendance = async () => {
    if (!filters.exam_id) {
      alert('Please select an exam to save attendance');
      return;
    }

    if (!filters.subject_id) {
      alert('Please select a subject to save attendance');
      return;
    }

    setSaving(true);
    try {
      const attendanceRecords = Object.entries(attendance).map(([studentId, status]) => ({
        student_id: studentId,
        status: status
      }));

      const response = await axios.post(
        `${API_URL}/api/examination/attendance/save`,
        {
          exam_id: filters.exam_id,
          subject_id: filters.subject_id,
          attendance: attendanceRecords
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAttendanceSaved(true);
      setIsEditMode(false);
      setSavedInfo({
        marked_by: response.data.marked_by || 'Admin',
        marked_at: new Date().toISOString()
      });
      
      alert(`✅ Attendance saved successfully for ${students.length} students!`);
      
    } catch (error) {
      console.error('Error saving attendance:', error);
      alert('❌ Failed to save attendance: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEnableEdit = () => {
    setIsEditMode(true);
    setAttendanceSaved(false);
  };

  const presentCount = Object.values(attendance).filter(s => s === 'Present').length;
  const absentCount = Object.values(attendance).filter(s => s === 'Absent').length;

  const filteredExams = exams.filter(exam => 
    (!filters.course_id || exam.course_id === filters.course_id) &&
    (!filters.semester_id || exam.semester_id === filters.semester_id)
  );

  const filteredSubjects = subjects.filter(sub =>
    (!filters.course_id || sub.course_id === filters.course_id) &&
    (!filters.semester_id || sub.semester_id === filters.semester_id)
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Exam Attendance</h1>
        <p className="text-sm text-gray-500 mt-1">Mark attendance for exam sessions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Students</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{students.length}</p>
            </div>
            <Users className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Present</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{presentCount}</p>
            </div>
            <Check className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Absent</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{absentCount}</p>
            </div>
            <X className="h-12 w-12 text-red-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Select Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.course_id} 
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Semester *
              {!filters.course_id && (
                <span className="text-xs text-gray-500 ml-2">(Select course first)</span>
              )}
            </label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.semester_id} 
              onChange={(e) => handleSemesterChange(e.target.value)}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Semester' : 'Select course first'}</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject *
              {!filters.course_id && (
                <span className="text-xs text-gray-500 ml-2">(Select course first)</span>
              )}
              {filters.course_id && filteredSubjects.length > 0 && (
                <span className="text-xs text-blue-600 ml-2">({filteredSubjects.length} subjects)</span>
              )}
            </label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.subject_id} 
              onChange={(e) => setFilters({ ...filters, subject_id: e.target.value })}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Subject' : 'Select course first'}</option>
              {filteredSubjects.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name} ({sub.code})</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button 
              onClick={loadStudents}
              disabled={!filters.course_id || !filters.semester_id || !filters.subject_id || loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Load Students'}
            </button>
          </div>
        </div>

        {students.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Exam (Required for saving)</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
                value={filters.exam_id} 
                onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
              >
                <option value="">Select Exam</option>
                {filteredExams.map(exam => (
                  <option key={exam.id} value={exam.id}>{exam.name} - {exam.exam_type}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {students.length > 0 ? (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Mark Attendance</h3>
              <div className="flex gap-2">
                {!attendanceSaved && !isEditMode && (
                  <>
                    <button
                      onClick={() => handleMarkAll('Present')}
                      className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
                    >
                      Mark All Present
                    </button>
                    <button
                      onClick={() => handleMarkAll('Absent')}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                    >
                      Mark All Absent
                    </button>
                  </>
                )}
                {attendanceSaved && !isEditMode ? (
                  <button
                    onClick={handleEnableEdit}
                    className="px-4 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Edit Attendance (Admin Only)
                  </button>
                ) : (
                  <button
                    onClick={handleSaveAttendance}
                    disabled={saving || !filters.exam_id}
                    className="px-4 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : isEditMode ? 'Update Attendance' : 'Save Attendance'}
                  </button>
                )}
              </div>
            </div>
            
            {attendanceSaved && !isEditMode && savedInfo && (
              <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <p className="text-sm font-semibold text-green-800">Attendance Saved</p>
                    <p className="text-xs text-green-700">
                      Marked by: {savedInfo.marked_by} at {new Date(savedInfo.marked_at).toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      ⚠️ Only Admin can edit this attendance
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {isEditMode && (
              <div className="bg-orange-50 border-l-4 border-orange-400 p-3 rounded">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-orange-500 mr-2" />
                  <div>
                    <p className="text-sm font-semibold text-orange-800">Edit Mode - Admin Only</p>
                    <p className="text-xs text-orange-700">Make changes and click "Update Attendance" to save</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roll No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission No</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Attendance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{student.roll_number || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{student.full_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{student.admission_number}</td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleAttendanceChange(student.id, 'Present')}
                          disabled={attendanceSaved && !isEditMode}
                          className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                            attendance[student.id] === 'Present'
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          } ${attendanceSaved && !isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Check className="h-4 w-4" />
                          Present
                        </button>
                        <button
                          onClick={() => handleAttendanceChange(student.id, 'Absent')}
                          disabled={attendanceSaved && !isEditMode}
                          className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                            attendance[student.id] === 'Absent'
                              ? 'bg-red-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          } ${attendanceSaved && !isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <X className="h-4 w-4" />
                          Absent
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select Course and Semester, then click "Load Students" to mark attendance</p>
        </div>
      )}
    </div>
  );
};

export default ExamAttendance;
